import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ExclamationTriangleIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { format, startOfWeek, endOfWeek, addWeeks } from "date-fns";
import { currentDateFnsLocale } from "../../../i18n/dateLocales";
import { NotesAdvancedSection } from "../notes/notesAdvancedSection";
import { ProjectAccessesSection } from "./ProjectAccessesSection";
import { ProjectContactsSection } from "./ProjectContactsSectionV2";
import { useProjectPermissions, useUserHasAccess } from "../../../routes/roleOnly";
import axios from "../../../api/axios";
import { mapsServiceUrl } from "../../Utils";
import { ProjectOverviewStats } from "./ProjectOverviewStats";
import { ProjectActivityFeed } from "./ProjectActivityFeed";
import { eventColorForId } from "./projectPlanUtils";
import { MapPinGlyph, MapGlyph } from "./projectRailUi";

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("nb-NO");
}

function mapsHref(address) {
  if (!address) return null;
  const query = [
    address.address,
    address.address2,
    address.postal || address.postal_code,
    address.place || address.city,
    address.country,
  ]
    .filter((part) => part && String(part).trim())
    .join(" ");
  if (!query) return null;
  return mapsServiceUrl(query);
}

function addressLine1(address) {
  if (!address) return "";
  return address.address || address.displayTitle || "";
}

function addressLine2(address) {
  if (!address) return "";
  return [address.postal || address.postal_code, address.place || address.city]
    .filter((part) => part && String(part).trim())
    .join(" ");
}

export const ProjectInfo = (props) => {
  const { t } = useTranslation();
  const { item, onRefresh } = props;

  const projectPermissions = useProjectPermissions(item);
  const canAccessProjectPlan = useUserHasAccess({ roles: ["projects_plan"] });
  const addressMapsHref = mapsHref(item?.delivery_address);

  const requiredFieldChecks = [
    { key: "name", label: t("project.required.field.name"), valid: !!item?.name },
    {
      key: "project_manager",
      label: t("project.required.field.project_manager"),
      valid: !!(item?.project_manager?.id || item?.project_manager_id),
    },
    { key: "start_date", label: t("project.required.field.start_date"), valid: !!item?.start_date },
  ];
  const missingRequiredFields = requiredFieldChecks.filter((f) => !f.valid);
  const showRequiredBanner =
    !!item?.id && projectPermissions?.canEdit && missingRequiredFields.length > 0;

  const canManagePeople =
    projectPermissions?.isAdmin ||
    projectPermissions?.canEdit ||
    projectPermissions?.canManageParticipants;

  return (
    <>
      {showRequiredBanner ? (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 shadow-sm">
          <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-amber-900">
                  {t("project.required.banner_title")}
                </h3>
                <p className="mt-1 text-sm text-amber-800">
                  {t("project.required.banner_description")}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {missingRequiredFields.map((f) => (
                    <span
                      key={f.key}
                      className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200"
                    >
                      {f.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <Link
              to={`/dashboard/projects/${item?.id}/edit`}
              className="inline-flex flex-shrink-0 items-center justify-center gap-1.5 rounded-md border border-transparent bg-amber-500 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
            >
              {t("project.required.edit_here")}
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      ) : null}

      <div className="pv-layout">
        <div className="pv-col">
          <ProjectOverviewStats project={item} />

          {canAccessProjectPlan ? <ProjectPlanPreview projectId={item?.id} /> : null}

          <ProjectActivityFeed project={item} />
        </div>

        <aside className="pv-rail">
          <div className="pv-addr">
            <MapPinGlyph />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13.5px] font-semibold">
                {addressLine1(item?.delivery_address) || t("project.overview.address.missing")}
              </div>
              <div className="truncate text-xs text-[#7a8798]">
                {addressLine2(item?.delivery_address) ||
                  item?.delivery_address?.country ||
                  t("project.overview.address.missing_city")}
              </div>
            </div>
            {addressMapsHref ? (
              <a href={addressMapsHref} target="_blank" rel="noreferrer" className="pv-map-btn">
                <MapGlyph />
                {t("project.overview.address.map")}
              </a>
            ) : projectPermissions.canEdit ? (
              <Link to={`/dashboard/projects/${item?.id}/edit`} className="pv-map-btn">
                {t("project.overview.address.edit")}
              </Link>
            ) : null}
          </div>

          <ProjectContactsSection
            variant="rail"
            project={item}
            onRefresh={onRefresh}
            canManage={canManagePeople}
          />

          <div className="pv-card" style={{ padding: "16px 18px 6px" }}>
            <span className="pv-kicker">{t("project.overview.facts.title")}</span>
            <div className="mt-2.5">
              <div className="pv-fact-row">
                <span className="pv-fact-k">{t("project.overview.fact.number")}</span>
                <span className="pv-fact-v">{item?.project_number || "—"}</span>
              </div>
              <div className="pv-fact-row">
                <span className="pv-fact-k">{t("project.overview.fact.category")}</span>
                <span className="pv-fact-v">{item?.project_category?.displayTitle || "—"}</span>
              </div>
              <div className="pv-fact-row">
                <span className="pv-fact-k">{t("project.overview.fact.department")}</span>
                <span className="pv-fact-v">{item?.department?.displayTitle || "—"}</span>
              </div>
              <div className="pv-fact-row">
                <span className="pv-fact-k">{t("project.overview.fact.customer")}</span>
                <span className="pv-fact-v">
                  {item?.customer?.id ? (
                    <Link to={`/dashboard/customers/${item.customer.id}`} className="pv-link">
                      {item.customer.displayTitle}
                    </Link>
                  ) : (
                    "—"
                  )}
                </span>
              </div>
              <div className="pv-fact-row">
                <span className="pv-fact-k">{t("project.overview.fact.manager")}</span>
                <span className="pv-fact-v">
                  {item?.project_manager?.id ? (
                    <Link to={`/dashboard/users/${item.project_manager.id}`} className="pv-link">
                      {item.project_manager.displayTitle}
                    </Link>
                  ) : (
                    "—"
                  )}
                </span>
              </div>
              <div className="pv-fact-row">
                <span className="pv-fact-k">{t("project.overview.fact.start")}</span>
                <span className="pv-fact-v">{formatDate(item?.start_date)}</span>
              </div>
              <div className="pv-fact-row">
                <span className="pv-fact-k">{t("project.overview.fact.end")}</span>
                <span className="pv-fact-v">
                  {item?.end_date ? formatDate(item.end_date) : t("project.overview.end_unset")}
                </span>
              </div>
              {item?.description ? (
                <div className="pv-fact-row" style={{ alignItems: "flex-start" }}>
                  <span className="pv-fact-k">{t("project.overview.fact.description")}</span>
                  <div className="prose prose-sm max-w-[200px] text-right text-[#33445a]">
                    <div dangerouslySetInnerHTML={{ __html: item.description }} />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <ProjectAccessesSection
            variant="rail"
            projectId={item?.id}
            project={item}
            projectPermissions={projectPermissions}
          />
        </aside>
      </div>

      {item?.id ? (
        <div className="mt-5">
          <NotesAdvancedSection
            module="project"
            module_param={item.id}
            legacyOnly
            title={t("notes.legacy_title")}
          />
        </div>
      ) : null}
    </>
  );
};

function eventMeta(event, t) {
  const parts = [];
  const start = event.start_date ? new Date(event.start_date) : null;
  if (start && !Number.isNaN(start.getTime())) {
    if (start.getHours() !== 0 || start.getMinutes() !== 0) {
      parts.push(`${t("project.plan.preview.time_prefix")} ${format(start, "HH:mm")}`);
    }
  }
  const n = event.resources?.length || 0;
  if (n > 0) {
    parts.push(
      `${n} ${n === 1 ? t("project.plan.preview.resource") : t("project.plan.preview.resources")}`
    );
  }
  return parts.join(" · ");
}

function eventTag(event) {
  return (
    event.activity?.displayTitle ||
    event.project_activity?.displayTitle ||
    event.work_order?.displayTitle ||
    event.status ||
    null
  );
}

const ProjectPlanPreview = ({ projectId }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const today = new Date();
        const weekStart = startOfWeek(today, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(addWeeks(today, 2), { weekStartsOn: 1 });
        const response = await axios.get("/project_events", {
          params: {
            project_id: projectId,
            date_from: format(weekStart, "yyyy-MM-dd"),
            date_to: format(weekEnd, "yyyy-MM-dd"),
            per_page: 10,
          },
        });
        if (response.data && response.data.data) {
          setEvents(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching project events:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [projectId]);

  if (!projectId) return null;

  return (
    <div className="pv-card">
      <div className="pv-card-head">
        <div>
          <h2>{t("project.plan.preview.title")}</h2>
          <p className="pv-card-sub">{t("project.plan.preview.subtitle")}</p>
        </div>
        <Link to={`/dashboard/projects/${projectId}/plan`} className="pv-link">
          {t("project.plan.preview.go_to_full_plan")}
        </Link>
      </div>
      <div className="px-5 pb-4 pt-2">
        {loading ? (
          <p className="py-6 text-center text-sm text-[#7a8798]">{t("project.plan.preview.loading")}</p>
        ) : error ? (
          <p className="py-6 text-center text-sm text-red-600">{t("project.plan.preview.error")}</p>
        ) : events.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#7a8798]">{t("project.plan.preview.no_events")}</p>
        ) : (
          <>
            {events.slice(0, 5).map((event) => {
              const start = event.start_date ? new Date(event.start_date) : null;
              const valid = start && !Number.isNaN(start.getTime());
              const tag = eventTag(event);
              return (
                <div key={event.id} className="pv-plan-row">
                  <div className="pv-plan-day">
                    <strong>{valid ? format(start, "dd") : "—"}</strong>
                    <span>{valid ? format(start, "MMM", { locale: currentDateFnsLocale() }) : ""}</span>
                  </div>
                  <div
                    className="pv-plan-bar"
                    style={{ background: eventColorForId(event.id) }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">
                      {event.title || t("project.plan.preview.untitled_event")}
                    </div>
                    <div className="text-xs text-[#7a8798]">{eventMeta(event, t)}</div>
                  </div>
                  {tag ? <span className="pv-chip">{tag}</span> : null}
                </div>
              );
            })}
            {events.length > 5 ? (
              <div className="pt-2 text-center">
                <Link to={`/dashboard/projects/${projectId}/plan`} className="pv-link">
                  {t("project.plan.preview.view_all", { count: events.length })}
                </Link>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
